import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button.js';
import {
  SecurityProcessMenu,
  MEDIUM_SECURITY_PAGES,
} from '../components/SecurityProcessMenu.js';
import { useCustomer } from '../customer-context.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import { isOtpLockedOut } from '../lib/action-denied.js';
import {
  beginMediumSecurity,
  commitMediumFallback,
} from '../lib/medium-security.js';
import infoIcon from '../assets/info-icon.svg';

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

type CheckStatus = 'pass' | 'fail' | null;

/**
 * Accessible pass/fail radio pair, faithful to the export's `AccessibleRadio`
 * (shared with the Medium / High / Outbound security ports).
 */
function PassFailRadios({
  name,
  value,
  onChange,
}: {
  name: string;
  value: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-6"
      role="radiogroup"
      aria-label={`${name} verification result`}
    >
      {(['pass', 'fail'] as const).map((option) => {
        const id = `${name}-${option}`;
        const checked = value === option;
        return (
          <div key={option} className="flex items-center gap-3">
            <input
              type="radio"
              id={id}
              name={name}
              checked={checked}
              onChange={() => onChange(option)}
              className="sr-only peer"
            />
            <label
              htmlFor={id}
              className="relative flex items-center cursor-pointer group"
            >
              <div className="relative size-[24px] rounded-full border-2 border-[#979c9f] transition-all bg-white group-hover:border-[#6b7280] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]">
                {checked && (
                  <div className="absolute inset-0 m-auto size-[12px] rounded-full bg-[#1d252c]" />
                )}
              </div>
              <span className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none capitalize">
                {option}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/** A labelled identity question with its value and pass/fail radios. */
function IdentityQuestion({
  label,
  value,
  name,
  status,
  onChange,
}: {
  label: string;
  value: string;
  name: string;
  status: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-4">
      <div className="flex flex-col">
        <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
          {label}
        </h3>
        <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
          {value}
        </p>
      </div>
      <div className="ml-auto mt-5">
        <PassFailRadios name={name} value={status} onChange={onChange} />
      </div>
    </div>
  );
}

interface OtpGenerateResult {
  sent: boolean;
  resultCode?: string;
  maskedMobile?: string;
  attemptsRemaining?: number;
  blocked?: boolean;
  message?: string;
}

interface OtpValidateResult {
  valid: boolean;
  resultCode?: string;
  message?: string;
  attemptsRemaining?: number;
  expired?: boolean;
  blocked?: boolean;
}

/** Maximum OTP validation attempts within the 5-minute window (CCF-106/16). */
const MAX_OTP_ATTEMPTS = 3;

/**
 * The customer's identity-question data (`POST /api/security/questions`,
 * bank-gateway PascalCase) — only the fields the ECL Medium fallback needs.
 */
interface SecurityQuestionsData {
  MobileNumber?: string;
  Landline?: string;
  EmailAddress?: string;
  ActiveVisaDebitCards?: string[];
}

/** Formats an ISO `YYYY-MM-DD` date of birth as `DD/MM/YYYY` for display. */
function formatDob(dob: string | undefined): string {
  if (!dob) return 'Not available';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dob;
}

/** Accessible dropdown for the active Visa debit cards (faithful to the export). */
function CardDropdown({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div
      className="flex flex-col gap-2"
      ref={ref}
      style={{ maxWidth: '260px' }}
    >
      <label
        htmlFor="ecl-visa-card"
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        Active Visa debit card numbers
      </label>
      <div className="relative">
        <button
          id="ecl-visa-card"
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={value ? '' : 'text-[#6b7280]'}>
            {/* #527/CCF-106: render the FULL 16-digit Visa debit card number
                the agent reads back to the customer (dylan219's sign-off scoped
                to THIS fallback question) — do NOT mask it here. */}
            {value ? value : 'Active Visa cards numbers'}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && options.length > 0 && (
          <ul
            role="listbox"
            aria-label="Active Visa debit card numbers"
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((card) => (
              <li
                key={card}
                role="option"
                aria-selected={value === card}
                tabIndex={0}
                onClick={() => {
                  onChange(card);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(card);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === card ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {/* #527/CCF-106: full 16-digit number, unmasked (see above). */}
                {card}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * #404: the tick box beside an answerable fallback question. The agent ticks the
 * ONE question they are answering; only then does that question's Pass/Fail radio
 * appear (single-select, handled by the parent).
 */
function FallbackTick({
  id,
  checked,
  onSelect,
}: {
  id: string;
  checked: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onSelect}
        className="size-[24px] rounded-[4px] border-2 border-[#343f41] accent-[#FF703E]"
      />
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] select-none cursor-pointer"
      >
        This is the question I am answering
      </label>
    </div>
  );
}

/** The three ordered ECL fallback questions (CCF-106). */
type FallbackKey = 'landline' | 'email' | 'visa';

/**
 * SCRN 54 — ECL Medium Security (CCF-106).
 *
 * Faithful port of the customer's Figma "update medium risk ECL" export. An
 * Enhanced Customer Support Agent (ECSA) authenticates a vulnerable customer
 * with an ECSA-style Medium-Risk check: Full name + Date of birth (each
 * Pass/Fail) plus the mandatory **2FA OTP** to the registered mobile (CCF-106).
 *
 * Fallback (CCF-106): when the customer cannot receive the 2FA text the agent
 * ticks **"Unable to complete 2FA"** and works the three additional questions in
 * the fixed order — landline, email, active Visa debit card. The agent needs
 * only **one** answered correctly to pass; a blank landline/email is skipped to
 * the next question, and any incorrect (Fail) answer is a failed authentication.
 *
 * Architecture: this is a SIBLING of the Medium/High security screens and
 * REUSES their existing Teller-ID-gated BFF routes — `/security/otp/generate` +
 * `/security/otp/validate` (the 2FA flow) and `/security/questions` (the
 * landline/email/active-Visa-debit-card data for the fallback). It adds NO new
 * client↔BFF or BFF↔gateway routes. Identity (name/DoB) is read from the
 * verified-customer context. Completion flips `securityLevel` to `'medium'`,
 * unlocking the Customer Dashboard.
 */
export function EclMediumSecurityScreen(): React.JSX.Element {
  const navigate = useNavigate();
  const { customer, updateCustomer } = useCustomer();

  // Admin-editable scripting text (content-catalogue keys); falls back to the
  // compiled-in defaults. Rendered as PLAIN TEXT only (no HTML).
  const eclMediumFailAdvisory = useContent('ecl.medium.security.fail.advisory');
  const eclMediumFailRemark = useContent('ecl.medium.security.fail.remark');

  const [fullNameStatus, setFullNameStatus] = useState<CheckStatus>(null);
  const [dobStatus, setDobStatus] = useState<CheckStatus>(null);

  // 2FA OTP state (reuses the SCRN-03 endpoints + CCF-16 lifecycle).
  const [otpSent, setOtpSent] = useState(false);
  const [maskedMobile, setMaskedMobile] = useState('');
  const [generating, setGenerating] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [validating, setValidating] = useState(false);
  const [otpStatus, setOtpStatus] = useState<'pass' | 'fail' | null>(null);
  const [otpAttemptsRemaining, setOtpAttemptsRemaining] =
    useState(MAX_OTP_ATTEMPTS);
  const [otpExpired, setOtpExpired] = useState(false);
  const [otpBlocked, setOtpBlocked] = useState(false);
  // Non-blocking inline warning for a wrong (non-final) OTP code (issue #164,
  // per the @dylan219 UX rule #80/#82/#94): a gateway-calling action's failure
  // must surface the code + message INLINE beside the action button, not only
  // as a transient toast, and stay retryable.
  const [otpError, setOtpError] = useState<{
    code?: string;
    message: string;
    // Which action produced the error, so it renders directly under THAT
    // button (#782): 'generate' → under Send Code; 'validate' → under Confirm.
    source: 'generate' | 'validate';
  } | null>(null);
  // #810: the gateway's (BFF-mapped, ruled #810 copy) message for the expired
  // (2004) state, rendered inline in the dedicated branch alert when present.
  const [otpExpiredMessage, setOtpExpiredMessage] = useState<string | null>(
    null,
  );

  // Fallback ("Unable to complete 2FA") state.
  const [unableTo2fa, setUnableTo2fa] = useState(false);
  const [questionData, setQuestionData] = useState<SecurityQuestionsData>({});
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  // The context key the questions were last loaded for (CCF-112, #477). Kept in
  // state — rather than relying on the bare `questionsLoaded` flag — so we can
  // derive purely from props whether the loaded data belongs to the CURRENT
  // customer/Teller context. This backs the stale-outcome guard on the terminal
  // Security Result event below: on a context swap it no longer matches the new
  // context until THIS customer's own /security/questions load re-stamps it.
  const [questionsContext, setQuestionsContext] = useState<string | null>(null);
  const [fallbackStatus, setFallbackStatus] = useState<
    Record<FallbackKey, CheckStatus>
  >({ landline: null, email: null, visa: null });
  const [selectedCard, setSelectedCard] = useState('');
  // #404 (@dylan219, option B + tick box): the agent ticks the ONE fallback
  // question they are answering. Only the selected question's Pass/Fail radios
  // (and, for Visa, the card dropdown) are shown. Single-select: ticking a
  // question replaces any prior selection.
  const [selectedFallback, setSelectedFallback] = useState<FallbackKey | null>(
    null,
  );

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const dobDisplay = formatDob(customer.dob);
  const hasDob = Boolean(customer.dob);
  const fullName = (customer.name ?? '').trim();
  const hasName = fullName.length > 0;
  // First name for the 2FA read-back script ("Thanks {name}, …"). The Figma
  // greets the customer by first name; fall back gracefully to a generic
  // greeting when no name is on the verified-customer context.
  const firstName = fullName.split(/\s+/)[0] ?? '';
  // ECL Medium gates its data loads + Send Code + upgrade on `customerNumber &&
  // tellerId` only — identical to the shared SecurityScreen — so a customer
  // reached via Customer Search (identified at level `none`, e.g. a Unibanks-only
  // customer) renders the identity questions and can run 2FA, exactly like the
  // other security screens (#787, CCF-106/107; accepted under #650/#609). It does
  // NOT add an extra `customerVerification === 'Y'` (prior-ID&V) precondition —
  // that was client-side defence-in-depth only; the BFF remains the enforcing
  // boundary (OTP/session-card/RBAC + fail-closed elevation, #363/#403).

  const landline = (questionData.Landline ?? '').trim();
  const email = (questionData.EmailAddress ?? '').trim();
  const activeCards = questionData.ActiveVisaDebitCards ?? [];

  // #368 (parity with the shared Security screen's #280 rule): the 2FA OTP is
  // delivered by SMS to the customer's registered mobile — with NO mobile on
  // file there is nothing to send the code to, so "Generate OTP" is a dead
  // action. Read mobile presence from the SAME authoritative /security/questions
  // data (loaded eagerly above), and only once it has loaded (no fail-open on a
  // pending/errored load). When the mobile is missing the screen surfaces an
  // inline warning that points the agent to the existing "Unable to complete
  // 2FA" fallback (ECL's alternative-verification path per CCF-106 — the
  // deliberate exception to the medium→high transition, @dylan219 on #287).
  const hasMobileOnFile = Boolean(questionData.MobileNumber?.trim());
  const mobileMissing = questionsLoaded && !hasMobileOnFile;

  // CCF-106: a fallback question can only be answered when its data is actually
  // on file (an authoritative /security/questions response). A field that is
  // blank/absent — including when the questions call has not (yet) returned, so
  // questionData is still {} — is "not on file" and CANNOT be marked Pass. This
  // prevents passing the second factor with no authoritative data to verify
  // against (esp. the Visa card, which previously was not data-gated).
  const landlineAvailable = landline.length > 0;
  const emailAvailable = email.length > 0;
  // Whether a card is on file decides the Visa sub-question's shape (confirm the
  // number vs. "no card" is the correct answer) — NOT whether the question can
  // be asked. The Visa question is the catch-all final fallback and is ALWAYS
  // answerable once the authoritative /security/questions data has loaded: per
  // CCF-106/Figma, "If no [card] — this is the correct answer." Gating it on
  // having a card would make a no-card customer impossible to authenticate.
  const hasCardOnFile = activeCards.length > 0;

  // #404 (@dylan219, option B): the agent chooses WHICH fallback question they
  // are answering by ticking its check box — not just the first available one.
  // A question can only be ticked (is "answerable") when its data is on file:
  // landline when `landlineAvailable`, email when `emailAvailable`, and the Visa
  // catch-all once the authoritative questions data has loaded (always answerable
  // then — with a card the agent confirms it, without one "no" is the correct
  // answer). A question with no data on file shows the "not on file / skip" note
  // and NO tick box, so it can never be selected/passed (#368/#366 invariant);
  // each question's answerability is gated inline on its data flag
  // (landlineAvailable / emailAvailable / questionsLoaded).

  // When the Visa question is the SELECTED question AND a card is on file, the
  // agent must first pick WHICH card they confirmed against — a bare "Pass"
  // with no selected card is not a complete confirmation (CCF-106). With no
  // card on file there is nothing to select ("no" is the correct answer).
  const visaSelectionOk =
    selectedFallback !== 'visa' || !hasCardOnFile || selectedCard !== '';

  // The agent-selected fallback question decides the second factor: a correct
  // answer passes; an incorrect one fails authentication.
  const fallbackPassed =
    selectedFallback !== null &&
    fallbackStatus[selectedFallback] === 'pass' &&
    visaSelectionOk;
  const anyFallbackFailed =
    selectedFallback !== null && fallbackStatus[selectedFallback] === 'fail';

  // The second factor is satisfied by EITHER a passing 2FA OTP OR — via the
  // fallback path — one correctly answered fallback question.
  const secondFactorOk = unableTo2fa ? fallbackPassed : otpStatus === 'pass';

  // ECL Medium completes ONLY when Name + DoB both Pass and the second factor is
  // satisfied — and only while a DoB is actually on file to have verified
  // against (no "pass" on data that isn't present).
  // Any identity Fail, a failed (3-attempt-exhausted) OTP, or any incorrect
  // fallback answer surfaces the branch-referral guidance (CCF-106 unhappy
  // path). These are TERMINAL and path-independent: once OTP has failed 3 times
  // or a fallback answer is wrong, switching paths must NOT rescue it.
  // #765 (@dylan219 authorised, 2026-09-07 — "ok go with option 2 Allow them
  // switch everything"): the agent may CORRECT a mis-selected answer within the
  // same session. Identity (Name/DoB) Pass/Fail and the fallback-question
  // selection are LIVE and recoverable — a Fail flipped back to Pass, or a
  // different fallback question ticked and passed, recomputes the outcome. There
  // is NO latch, reversing the #404 anti-"fishing" latch per the ruling above, so
  // the agent no longer has to Clear Session to undo a mis-click.
  //
  // The ONE failure that stays terminal is the 2FA OTP gateway lockout (#639): 3
  // wrong codes / a blocked generate is a real 24h SERVER-side lockout driven by
  // `otpStatus==='fail'`/`otpBlocked` — gateway state, not a radio — so it is
  // sticky by construction (cleared only by a fresh OTP generate or a context
  // reset) and the fallback path cannot rescue it (see `handleUnableTo2faChange`).
  const anyFailed =
    fullNameStatus === 'fail' ||
    dobStatus === 'fail' ||
    otpStatus === 'fail' ||
    otpBlocked ||
    anyFallbackFailed;

  // #263: the FAIL verdict must not be PRESENTED until ALL required security
  // steps are complete — Full name + DoB answered AND the second factor at a
  // terminal outcome. On the OTP path that means a passing OTP or a terminal
  // (3-attempts-exhausted / blocked) failure; on the "unable to 2FA" fallback
  // path it means the single active fallback question has been answered. A step
  // still pending shows in-progress, never a premature Fail. The FAIL verdict now
  // tracks the LIVE `anyFailed` (#765, no latch); this only gates WHEN it is
  // presented (the FAIL badge + branch-referral) until every step is complete.
  const secondFactorComplete = unableTo2fa
    ? // The agent-selected fallback question has been answered (a fail resolves
      // the path; a pass also needs a Visa selection where a card is on file).
      anyFallbackFailed ||
      (selectedFallback !== null &&
        fallbackStatus[selectedFallback] === 'pass' &&
        visaSelectionOk)
    : otpStatus === 'pass' || otpStatus === 'fail' || otpBlocked;
  const allStepsComplete =
    fullNameStatus !== null && dobStatus !== null && secondFactorComplete;
  const showFail = anyFailed && allStepsComplete;

  // Verification requires all positive checks AND no LIVE failure (#765: the
  // agent can correct a mis-click, so this recomputes rather than latching).
  // Because `allChecksPass` gates the "Continue to dashboard" CTA, switching any
  // answer to Fail removes the button immediately (point 2, #765).
  const allChecksPass =
    fullNameStatus === 'pass' &&
    hasName &&
    dobStatus === 'pass' &&
    hasDob &&
    secondFactorOk &&
    !anyFailed;

  // Whether the loaded question data belongs to the CURRENT customer/Teller
  // context (CCF-112, #477). Derived PURELY from props (`questionsContext` is
  // stamped only when THIS context's /security/questions load returns), so it
  // flips to `false` on the very first render after a context swap — before any
  // effect re-runs — and only becomes `true` again once the new customer's own
  // data has loaded. This is the ECL analogue of the shared Medium screen's
  // `mobileForContext` guard.
  const questionsForContext =
    questionsLoaded && questionsContext === `${customerNumber}|${tellerId}`;

  function bankHeaderParams(): { header: { 'X-Teller-Id': string } } {
    return { header: { 'X-Teller-Id': tellerId } };
  }

  // CCF-112 (#477): record the ECL Medium screen's TERMINAL outcome (Passed /
  // Failed) as a Non-API business event in Call History — the SAME event the
  // shared Medium/High security screens already fire. Both outcomes are pure
  // client-side screen states, so — mirroring the standard variants — they are
  // fired best-effort here, exactly once per terminal outcome. A once-guard
  // (keyed to the customer + Teller context) prevents a re-fire on re-render and
  // resets when the context changes, so a fresh customer is recorded again. A
  // Call-History write failure must NEVER break the flow (`.catch`).
  //
  // Stale-outcome-crosses-customers guard (#243/#244/#422 bug class): on a
  // context swap the per-customer reset effect below and this effect run in the
  // SAME commit, so on that render `customerNumber` is already the NEW CIF while
  // `allChecksPass`/`showFail` still derive from the PREVIOUS customer's
  // not-yet-cleared statuses — posting then would write a false Security Result
  // against the wrong customer. `questionsForContext` is derived purely from
  // props and flips to `false` on the first render of a new context, staying
  // false until THIS customer's own /security/questions load re-stamps it, so a
  // terminal outcome can never be attributed to the wrong customer. (A terminal
  // outcome is itself unreachable without loaded question data, so this cannot
  // suppress a genuine event.)
  const securityEventFiredRef = React.useRef<string | null>(null);
  const lastContextKey = `${customerNumber}|${tellerId}`;
  React.useEffect(() => {
    if (securityEventFiredRef.current === lastContextKey) return;
    if (!customerNumber || !questionsForContext) return;
    // Terminal Pass = all checks pass; terminal Fail = every step complete but
    // the run did not pass (`showFail`). Neither fires while a step is pending.
    const outcome: 'Passed' | 'Failed' | null = allChecksPass
      ? 'Passed'
      : showFail
        ? 'Failed'
        : null;
    if (!outcome) return;
    securityEventFiredRef.current = lastContextKey;
    void api
      .POST('/calls/security-event', {
        params: bankHeaderParams(),
        body: { customerNumber, securityLevel: 'medium', outcome },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
    // `bankHeaderParams`/`lastContextKey` close over `tellerId`, already part of
    // the context key; excluded to keep the effect keyed to the terminal-outcome
    // transition for the current customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChecksPass, showFail, customerNumber, questionsForContext]);

  // Fail-closed on context change (CCF-4, parity with the shared Medium/High +
  // ECL High screens): when the identified customer/Teller context changes while
  // this screen stays mounted, reset ALL per-customer verification + OTP +
  // fallback state so a previous customer's Pass results / loaded questions / a
  // sent OTP can never carry over to a new CIF. Clearing `questionsLoaded`/
  // `questionsContext` also re-arms the fail-closed gate (and the #477 terminal
  // Security Result guard) until the new customer's authoritative data loads.
  const lastResetContext = React.useRef(lastContextKey);
  useEffect(() => {
    if (lastResetContext.current === lastContextKey) return;
    lastResetContext.current = lastContextKey;
    setFullNameStatus(null);
    setDobStatus(null);
    setOtpSent(false);
    setMaskedMobile('');
    setOtpCode('');
    setOtpStatus(null);
    setOtpAttemptsRemaining(MAX_OTP_ATTEMPTS);
    setOtpExpired(false);
    setOtpBlocked(false);
    setOtpError(null);
    setUnableTo2fa(false);
    setQuestionData({});
    setQuestionsLoaded(false);
    setQuestionsContext(null);
    setFallbackStatus({ landline: null, email: null, visa: null });
    setSelectedCard('');
    setSelectedFallback(null);
  }, [lastContextKey]);

  function ensureContext(): boolean {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) {
      toast.error('No verified customer in context. Complete ID&V first.');
      return false;
    }
    return true;
  }

  // Fetch the security-question data (mobile / landline / email / active Visa
  // debit cards) once we have a verified customer + Teller ID. Reuses the
  // existing `/security/questions` route.
  //
  // #368: this is loaded EAGERLY (no longer gated on the fallback opt-in) so the
  // screen can surface the "no mobile on file" warning up-front — parity with
  // the shared Security screen's #280 `mobileMissing` guidance — instead of the
  // agent only discovering the dead-end after a failed OTP generate. The same
  // response drives the "Unable to complete 2FA" fallback (landline / email /
  // Visa), so one call serves both. Gated on `customerNumber && tellerId` only
  // (mirrors the shared SecurityScreen, #787).
  useEffect(() => {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    let cancelled = false;
    void (async () => {
      try {
        const { data, response } = await api.POST('/security/questions', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (!response.ok || !data) return;
        if (!cancelled) {
          // Mask card identifiers at INGESTION so a raw PAN never enters client
          // state (no-PAN-leak invariant, defence-in-depth): even if a
          // misconfigured BFF/gateway returns full numbers, only the masked
          // `•••• 1234` form is ever held in `questionData`/`selectedCard` or
          // used as a React key — not just masked at render time.
          const raw = data as SecurityQuestionsData;
          // Issue #527 (CCF-106): the ECL Medium fallback question asks the
          // customer to confirm the FULL 16-digit Visa debit card number, so —
          // by @dylan219's explicit sign-off scoped to THIS question — the
          // active-Visa-debit-card numbers are held and rendered UNMASKED here
          // (the BFF sources them the same way the dashboard does, #527). This
          // is the ONE deliberate exception to the screen's no-PAN-leak masking
          // (all other card identifiers on this screen stay masked); the full
          // number cannot otherwise be read back to the customer.
          setQuestionData({
            ...raw,
            ActiveVisaDebitCards: raw.ActiveVisaDebitCards ?? [],
          });
          // Authoritative data is now in hand: the Visa catch-all becomes
          // answerable (only reached if landline + email are not on file).
          setQuestionsLoaded(true);
          // Stamp the context this data belongs to (CCF-112, #477) so the
          // terminal Security Result event can only fire once THIS customer's
          // own data has loaded — never against a stale prior-customer outcome.
          setQuestionsContext(`${customerNumber}|${tellerId}`);
        }
      } catch {
        // Non-fatal: labels fall back to "Not on file".
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerNumber, tellerId]);

  // Begin a server-side Medium Security run (issue #403) as soon as the ECL
  // Medium screen opens for an identified customer — the seam the BFF uses to
  // bind the "Unable to complete 2FA" sanctioned skip to a genuine medium flow,
  // so a bare identified session cannot elevate directly and a forged completion
  // body reason is ignored. Best-effort; the server still fail-closes at the
  // fallback completion if the run was not recorded.
  useEffect(() => {
    if (!customerNumber || !/^\d{4}$/.test(tellerId)) return;
    void beginMediumSecurity(customerNumber, tellerId);
  }, [customerNumber, tellerId]);

  // NOTE: medium verification is COMMITTED on the explicit "Continue to
  // dashboard" action (see the CTA handler), NOT speculatively in an effect on
  // `allChecksPass`. This avoids leaving the shared customer context flagged
  // `medium` if the agent changes an answer to Fail (or toggles paths) after the
  // checks momentarily passed — i.e. verification state is never granted then
  // left stale/un-revoked.

  /** Commit ECL medium verification and proceed to the dashboard. */
  async function completeAndContinue(): Promise<void> {
    if (!allChecksPass) return;
    // #403: the OTP path (`!unableTo2fa`) already raised the server-side session
    // to `medium` inside `/security/otp/validate`, so it needs no extra call. But
    // the "Unable to complete 2FA" FALLBACK path validated no OTP, so nothing
    // raised the server session — the dashboard's `requireVerification('medium')`
    // servicing routes would then 403 ("Complete security verification first").
    // Record `medium` server-side via the sanctioned, audited fallback seam FIRST,
    // and abort the navigation if it does not land (so we never leave the client
    // showing "verified" while the server refuses servicing).
    if (unableTo2fa) {
      const recorded = await commitMediumFallback(customerNumber, tellerId);
      if (!recorded) {
        toast.error(
          'Medium security could not be recorded. Please complete the checks again.',
        );
        return;
      }
    }
    // #427: name the ECL flow in the banner ("ECL Medium Security Verified").
    updateCustomer({
      securityLevel: 'medium',
      customerVerification: 'Y',
      verificationVariant: 'ECL',
    });
    toast.success('Customer verified (ECL medium security).');
    navigate('/dashboard');
  }

  // When the agent toggles the fallback path, reset the OTHER path's result so a
  // stale pass on the abandoned path can't survive (CCF-106: the agent works one
  // path at a time). Switching back into 2FA clears the fallback answers; opting
  // into the fallback clears any prior OTP pass/fail.
  function handleUnableTo2faChange(next: boolean): void {
    setUnableTo2fa(next);
    if (next) {
      // Clear a non-terminal OTP state so switching to the fallback starts
      // clean — but a TERMINAL OTP failure (3 attempts exhausted / blocked) is
      // NOT cleared: once 2FA has terminally failed, the fallback must not rescue
      // it (#639 — a real 24h gateway lockout, not a correctable radio).
      if (otpStatus !== 'fail' && !otpBlocked) setOtpStatus(null);
    } else {
      // Switching back to OTP clears ALL fallback state, including a failed
      // fallback answer (#765: @dylan219 "allow them switch everything" — a
      // fallback failure is a correctable question answer, not terminal).
      setFallbackStatus({ landline: null, email: null, visa: null });
      setSelectedCard('');
      setSelectedFallback(null);
    }
  }

  // #404: the agent ticks the ONE question they are answering (single-select).
  // Ticking replaces any prior selection AND resets the previously-selected
  // question's Pass/Fail status so a stale answer can't carry over. #765
  // (@dylan219 "allow them switch everything"): there is NO latch, so ticking a
  // different question after a Fail and passing it DOES recover the run — the
  // outcome tracks the currently-selected question live. Selecting a new question
  // also clears any Visa card selection (only relevant while Visa is selected).
  function handleSelectFallback(key: FallbackKey): void {
    setSelectedFallback((prev) => {
      if (prev !== null && prev !== key) {
        setFallbackStatus((s) => ({ ...s, [prev]: null }));
      }
      return key;
    });
    setSelectedCard('');
  }

  async function handleGenerateOtp(): Promise<void> {
    if (!ensureContext()) return;
    setGenerating(true);
    try {
      const { data, error, response } = await api.POST(
        '/security/otp/generate',
        {
          params: bankHeaderParams(),
          body: { customerNumber },
        },
      );
      if (response.status === 403) {
        // #788 (CCF-16) — a terminal OTP lockout (#639) fails closed with
        // 403 `{ error: 'otp_locked_out' }`. Route it to the dedicated inline
        // CCF-16 block alert (not the generic RBAC/teller toast).
        if (isOtpLockedOut(response.status, error)) {
          setOtpBlocked(true);
          return;
        }
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { sent: false }) as OtpGenerateResult;
      if (body.sent) {
        setOtpSent(true);
        setMaskedMobile(body.maskedMobile ?? '');
        setOtpStatus(null);
        setOtpCode('');
        setOtpExpired(false);
        setOtpBlocked(false);
        setOtpError(null);
        setOtpAttemptsRemaining(body.attemptsRemaining ?? MAX_OTP_ATTEMPTS);
        toast.success(
          body.maskedMobile
            ? `Code sent to ${body.maskedMobile}`
            : 'One-time passcode sent',
        );
      } else if (body.blocked) {
        setOtpBlocked(true);
        toast.error(
          body.message ??
            'OTP generation is blocked for 24 hours after 3 failed attempts.',
        );
      } else {
        // Send Code (generate) failed — surface the gateway code + message
        // INLINE under the Send Code button (#782), not only as a toast.
        const message = body.message ?? 'Could not send the one-time passcode.';
        setOtpError({ code: body.resultCode, message, source: 'generate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'generate' });
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleValidateOtp(): Promise<void> {
    if (!ensureContext()) return;
    if (!/^\d{6}$/.test(otpCode)) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setValidating(true);
    // Clear any prior inline warning at the start of a fresh validate attempt.
    setOtpError(null);
    try {
      const { data, response } = await api.POST('/security/otp/validate', {
        params: bankHeaderParams(),
        body: { customerNumber, otp: otpCode },
      });
      if (response.status === 403) {
        toast.error('Forbidden: check Teller ID and permissions.');
        return;
      }
      const body = (data ?? { valid: false }) as OtpValidateResult;
      if (body.valid) {
        setOtpStatus('pass');
        setOtpError(null);
        toast.success('Two-factor authentication passed.');
        return;
      }
      if (typeof body.attemptsRemaining === 'number') {
        setOtpAttemptsRemaining(body.attemptsRemaining);
      }
      if (body.expired) {
        // CCF-106: the 5-minute window elapsed — issue a new OTP (retryable, not
        // a security failure). The expired branch renders its own dedicated
        // inline alert — clear the generic wrong-code warning so they don't
        // double up. #810: prefer the BFF-mapped ruled copy.
        const expiredMessage =
          body.message ??
          'The one-time passcode has expired. Generate a new code.';
        setOtpExpired(true);
        setOtpSent(false);
        setOtpError(null);
        setOtpExpiredMessage(expiredMessage);
        toast.error(expiredMessage);
      } else if (body.blocked) {
        // CCF-106: 3 incorrect attempts — only NOW does 2FA fail (this drives
        // the security-failed/referral guidance via `anyFailed`). The blocked
        // branch has its own dedicated inline alert — clear the generic
        // wrong-code warning so they don't double up.
        setOtpStatus('fail');
        setOtpBlocked(true);
        setOtpSent(false);
        setOtpError(null);
        toast.error(
          body.message ??
            'Incorrect code 3 times. Security has failed — advise the customer to call again.',
        );
      } else {
        // A non-final wrong code: allow retry (do NOT set otpStatus to 'fail',
        // which would prematurely trigger the security-failed branch). Surface
        // the gateway code + message INLINE beside the Confirm button as a
        // non-blocking warning (issue #164, per #80/#82/#94), KEEPING the toast.
        const remaining = body.attemptsRemaining;
        const message =
          body.message ??
          (typeof remaining === 'number'
            ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
            : 'The code does not match. Please retry.');
        setOtpError({ code: body.resultCode, message, source: 'validate' });
        toast.error(message);
      }
    } catch {
      const message = 'Request failed.';
      setOtpError({ message, source: 'validate' });
      toast.error(message);
    } finally {
      setValidating(false);
    }
  }

  function setFallback(key: FallbackKey, v: 'pass' | 'fail'): void {
    setFallbackStatus((s) => ({ ...s, [key]: v }));
  }

  const otpFieldClasses =
    "px-4 py-3 border-2 rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] placeholder:text-[#6b7280] transition-all focus:outline-none focus:ring-4 focus:border-[#005fcc] focus:ring-[#005fcc]/20 disabled:bg-[#f3f4f6] disabled:text-[#6b7280] disabled:cursor-not-allowed hover:border-[#979c9f] border-[#dad7d3]";
  const primaryBtn =
    "bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]";

  // #517: ECL Medium (SCRN-54) entry — used to render the inline "Go to ECL
  // High Security" button when mobile is on file and the agent is not on the
  // unable-to-2FA fallback path.
  const eclHighEntry = MEDIUM_SECURITY_PAGES.find((p) => p.id === 'SCRN-54');

  return (
    <main
      className="max-w-[800px] pl-12 pr-12 py-12"
      aria-labelledby="ecl-medium-heading"
    >
      <div className="mb-6 flex items-start justify-between gap-8 flex-wrap">
        <h1
          id="ecl-medium-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#FF703E] m-0"
        >
          ECL: Medium Security
        </h1>
        {/* Medium security pages cross-navigate (issue #101 / @dylan219): the
            dropdown offers the OTHER medium pages + this page's own relevant
            high page (ECL High). Pure navigation — each target is an existing
            RBAC-gated route, re-checked on arrival (deny-by-default). */}
        <SecurityProcessMenu currentId="SCRN-54" />
      </div>

      <div className="space-y-6">
        {/* Medium Security verification section — the identity questions
            (Full name + Date of birth). Per the approved Figma (#566), the 2FA
            step is a SEPARATE section below this panel, not part of it. */}
        <InfoBox>
          <div className="space-y-6">
            {/* Identity questions: Full name + Date of birth (CCF-106). */}
            <div className="space-y-6">
              <IdentityQuestion
                label="Full name:"
                value={hasName ? fullName : 'Not available'}
                name="full-name"
                status={fullNameStatus}
                onChange={setFullNameStatus}
              />
              <div className="border-t-2 border-[#dad7d3]" />
              <IdentityQuestion
                label="Date of birth:"
                value={dobDisplay}
                name="dob"
                status={dobStatus}
                onChange={setDobStatus}
              />
            </div>
          </div>
        </InfoBox>

        {/* 2FA OTP step — a SEPARATE, clearly-delineated section AFTER the
            Medium Security verification section (approved Figma, #566), to the
            registered mobile (CCF-106). Disabled once the agent has opted into
            the fallback path. */}
        <InfoBox>
          <div className="space-y-6">
            <div>
              {/* Read-back scripting (approved Figma, #566): greet the customer
                  by first name, explain the extra security check, and ask for
                  their number before sending the code. */}
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-4">
                {firstName
                  ? `Thanks ${firstName}, I need to do one more security check. I'll send a code to your mobile phone.`
                  : `Thanks, I need to do one more security check. I'll send a code to your mobile phone.`}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-4">
                Can you tell me your phone number please?
              </p>
              <div className="flex items-baseline gap-2 mb-4">
                <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                  Customer mobile number:
                </h3>
                {/* CCF-106 / #528: show the on-file mobile BEFORE send-code —
                    the agent reads it out and confirms the customer can receive
                    an OTP on it — from the already-fetched /security/questions
                    (`questionData.MobileNumber`, the same source as
                    `hasMobileOnFile`/`mobileMissing`). Once a code has been
                    sent, prefer the (possibly differently-formatted)
                    `maskedMobile` the gateway echoed back on
                    `/security/otp/generate`. Parity with the shared Security
                    screen. */}
                {(maskedMobile ||
                  (questionsLoaded && questionData.MobileNumber)) && (
                  <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                    {maskedMobile || questionData.MobileNumber}
                  </p>
                )}
              </div>

              {/* #368: when NO mobile is on file the OTP cannot be delivered, so
                  warn the agent up-front (parity with the shared Security
                  screen's #280 guidance) and point them to the existing "Unable
                  to complete 2FA" fallback below — ECL's alternative-verification
                  path (CCF-106) is the deliberate exception to the medium→high
                  transition (@dylan219, #287), so there is NO "move to high"
                  control here. Suppressed once the agent has opted into the
                  fallback (its own questions then take over). */}
              {mobileMissing && !unableTo2fa && (
                <p
                  role="alert"
                  className="text-[16px] font-['Arial',sans-serif] text-[#dc2626] mt-0 mb-4"
                >
                  No mobile number is on file for this customer, so an OTP
                  cannot be sent. Tick “Unable to complete 2FA” below to verify
                  the customer with the alternative security questions instead.
                </p>
              )}

              <div className="mt-4">
                <Button
                  type="button"
                  onClick={() => void handleGenerateOtp()}
                  disabled={
                    generating ||
                    otpStatus === 'pass' ||
                    otpBlocked ||
                    unableTo2fa ||
                    mobileMissing
                  }
                  aria-label="Generate one-time passcode"
                  className={primaryBtn}
                >
                  {generating
                    ? 'Generating…'
                    : otpSent
                      ? 'Resend OTP'
                      : 'Send Code'}
                </Button>
              </div>

              {/* Send Code (generate) failure — gateway code + message shown
                  INLINE directly under the Send Code button (#782), so the
                  error is obvious right where the agent clicked. Validate
                  failures render under the Confirm button instead (below). */}
              {otpError &&
                otpError.source === 'generate' &&
                !unableTo2fa &&
                otpStatus !== 'pass' &&
                !otpExpired &&
                !otpBlocked && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                  >
                    {otpError.code
                      ? `(${otpError.code}) ${otpError.message}`
                      : otpError.message}
                  </p>
                )}

              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mt-4 mb-4">
                I just sent the code to your phone. When you get it, can you
                read it back to me?
              </p>

              <label
                htmlFor="otp-code"
                className="block text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2"
              >
                Verification number
              </label>

              <div className="flex items-center gap-4 flex-wrap">
                <input
                  id="otp-code"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="Enter verification number"
                  value={otpCode}
                  disabled={!otpSent || otpStatus === 'pass' || unableTo2fa}
                  onChange={(e) =>
                    setOtpCode(
                      e.target.value.replace(/[^0-9]/g, '').slice(0, 6),
                    )
                  }
                  style={{ width: '220px' }}
                  className={otpFieldClasses}
                />

                <Button
                  type="button"
                  onClick={() => void handleValidateOtp()}
                  disabled={
                    !otpSent ||
                    otpCode.length !== 6 ||
                    validating ||
                    otpStatus === 'pass' ||
                    unableTo2fa
                  }
                  aria-label="Validate one-time passcode"
                  className={primaryBtn}
                >
                  {validating ? 'Validating…' : 'Confirm'}
                </Button>

                {/* Overall security outcome (CCF-106): the flag reflects the
                    WHOLE verification — PASS only when every identity check AND
                    2FA pass, FAIL if any Pass/Fail question fails or 2FA fails
                    (live `anyFailed`, #765 — recomputed as the agent corrects an
                    answer, not latched). It must not represent the 2FA result
                    alone. (Hidden on the unable-to-2FA fallback path, which has
                    its own outcome handling.) */}
                {!unableTo2fa && allChecksPass && (
                  <span
                    className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                    aria-label="Verification passed"
                  >
                    Pass
                  </span>
                )}
                {/* #263: only presented once every step (incl. 2FA) is done. */}
                {!unableTo2fa && showFail && (
                  <span
                    className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                    aria-label="Verification failed"
                  >
                    Fail
                  </span>
                )}
              </div>

              {/* Validate (Confirm) failure — a wrong (non-final) code: gateway
                  code + message shown INLINE directly under the Confirm button
                  (#782), where the agent just clicked. Stays retryable. The
                  expired / blocked branches render their own alerts below. */}
              {otpError &&
                otpError.source === 'validate' &&
                !unableTo2fa &&
                otpStatus !== 'pass' &&
                !otpExpired &&
                !otpBlocked && (
                  <p
                    role="alert"
                    className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                  >
                    {otpError.code
                      ? `Incorrect code (${otpError.code}): ${otpError.message}`
                      : otpError.message}
                  </p>
                )}

              {otpSent &&
                !unableTo2fa &&
                !otpBlocked &&
                !otpExpired &&
                otpStatus !== 'pass' && (
                  <p
                    role="status"
                    aria-live="polite"
                    className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-3 mb-0"
                  >
                    {`OTP attempts remaining: ${otpAttemptsRemaining}`}
                  </p>
                )}
              {otpExpired && !unableTo2fa && (
                <p
                  role="status"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                >
                  {otpExpiredMessage ??
                    'The one-time passcode has expired. Generate a new code to continue.'}
                </p>
              )}
              {otpBlocked && !unableTo2fa && (
                <p
                  role="alert"
                  className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] mt-3 mb-0"
                >
                  The one-time passcode was entered incorrectly 3 times.
                  Security has failed — advise the customer to call again and
                  record the failure.
                </p>
              )}
            </div>

            {/* Fallback opt-in (CCF-106). #639: once the customer has entered the
                OTP incorrectly on the 3rd attempt (`otpBlocked`), 2FA has
                terminally FAILED — the "Unable to complete 2FA" fallback must
                NOT be tickable (it cannot rescue a failed run). Disable it so
                the screen simply shows the FAIL state, matching the shared
                high-security screen's skip-with-reason control (which is already
                disabled on `otpBlocked`). */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="unable-2fa"
                checked={unableTo2fa}
                disabled={otpBlocked}
                onChange={() => handleUnableTo2faChange(!unableTo2fa)}
                className="size-[24px] rounded-[4px] border-2 border-[#343f41] accent-[#FF703E] disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label
                htmlFor="unable-2fa"
                className={`text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none ${otpBlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                Unable to complete 2FA
              </label>
            </div>
          </div>
        </InfoBox>

        {/* #517: when a mobile number IS on file (and the agent hasn't activated
            the unable-to-2FA fallback), surface a direct "Go to ECL High
            Security" option. Mirrors the `mediumHighTransition` block in
            SecurityScreen.tsx used by VISA/BUS24 Medium (SCRN-04/05). */}
        {questionsLoaded && hasMobileOnFile && !unableTo2fa && eclHighEntry && (
          <div className="rounded-md border-2 border-[#dad7d3] bg-[#faf9f7] p-4">
            <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mt-0 mb-2">
              Proceed to high security?
            </p>
            <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mt-0 mb-3">
              If higher-level verification is required, transition to{' '}
              {eclHighEntry.highLabel}.
            </p>
            <Button
              type="button"
              onClick={() => navigate(eclHighEntry.highRoute)}
              aria-label={`Go to ${eclHighEntry.highLabel}`}
              className={primaryBtn}
            >
              Go to {eclHighEntry.highLabel}
            </Button>
          </div>
        )}

        {/* Fallback questions (CCF-106) — shown when 2FA cannot be completed.
            Asked in order; 1 correct passes security. */}
        {unableTo2fa && (
          <>
            <div className="bg-[#1D252C] text-white px-6 py-3 inline-block">
              <h2 className="text-[18px] font-['Arial',sans-serif] font-bold m-0">
                Tick the question you are answering, then record whether the
                customer answered it correctly. Only one question needs to be
                answered correctly. A question with no answer on file cannot be
                ticked.
              </h2>
            </div>

            <InfoBox>
              <div className="space-y-6">
                {/* Q1 — Landline. Answerable only when on file; the agent ticks
                    it to reveal its Pass/Fail radios (#404). */}
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col">
                      <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                        Can you confirm the landline you have on file:
                      </h3>
                      <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                        {landlineAvailable ? landline : 'Not on file'}
                      </p>
                    </div>
                    {landlineAvailable && (
                      <FallbackTick
                        id="ecl-landline-tick"
                        checked={selectedFallback === 'landline'}
                        onSelect={() => handleSelectFallback('landline')}
                      />
                    )}
                  </div>
                  <div className="ml-auto mt-5">
                    {landlineAvailable ? (
                      selectedFallback === 'landline' && (
                        <PassFailRadios
                          name="ecl-landline"
                          value={fallbackStatus.landline}
                          onChange={(v) => setFallback('landline', v)}
                        />
                      )
                    ) : (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                        role="note"
                      >
                        No landline on file — skip to the next question.
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#e5e7eb]" />

                {/* Q2 — Email. Answerable only when on file; the agent ticks it
                    to reveal its Pass/Fail radios (#404) — selectable even when a
                    landline is also on file (option B). */}
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col">
                      <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                        Can you confirm the email address on file:
                      </h3>
                      <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                        {emailAvailable ? email : 'Not on file'}
                      </p>
                    </div>
                    {emailAvailable && (
                      <FallbackTick
                        id="ecl-email-tick"
                        checked={selectedFallback === 'email'}
                        onSelect={() => handleSelectFallback('email')}
                      />
                    )}
                  </div>
                  <div className="ml-auto mt-5">
                    {emailAvailable ? (
                      selectedFallback === 'email' && (
                        <PassFailRadios
                          name="ecl-email"
                          value={fallbackStatus.email}
                          onChange={(v) => setFallback('email', v)}
                        />
                      )
                    ) : (
                      <p
                        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                        role="note"
                      >
                        No email on file — skip to the next question.
                      </p>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#e5e7eb]" />

                {/* Q3 — Active Visa debit card (the catch-all). Answerable once
                    the authoritative questions data has loaded — with a card the
                    agent confirms the number, without one "no" is the correct
                    answer (CCF-106). The agent ticks it to reveal the card
                    dropdown (where a card is on file) + its Pass/Fail radios
                    (#404). */}
                <div className="flex items-start gap-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-col">
                      <h3 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                        Do you have an active Visa debit card?
                      </h3>
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-1">
                        If yes — confirm the full 16-digit card number.
                      </p>
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-4">
                        If no — this is the correct answer.
                      </p>
                      {hasCardOnFile ? (
                        selectedFallback === 'visa' && (
                          <CardDropdown
                            value={selectedCard}
                            onChange={setSelectedCard}
                            options={activeCards}
                          />
                        )
                      ) : (
                        <p
                          className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] m-0"
                          role="note"
                        >
                          No active Visa debit cards on file.
                        </p>
                      )}
                    </div>
                    {questionsLoaded && (
                      <FallbackTick
                        id="ecl-visa-tick"
                        checked={selectedFallback === 'visa'}
                        onSelect={() => handleSelectFallback('visa')}
                      />
                    )}
                  </div>
                  {selectedFallback === 'visa' && (
                    <div className="ml-auto mt-1">
                      <PassFailRadios
                        name="ecl-visa"
                        value={fallbackStatus.visa}
                        onChange={(v) => setFallback('visa', v)}
                      />
                    </div>
                  )}
                </div>

                {/* #762: green "Pass" indicator for the fallback path, mirroring
                    the OTP-path box above (`!unableTo2fa && allChecksPass`). The
                    figma shows a green Pass box at the bottom of the additional-
                    questions card once security has been satisfied via the
                    fallback. Gated on `allChecksPass` (Name + DoB pass AND the
                    fallback second factor passed AND prior ID&V) so it never
                    shows while any check is still failing/outstanding. */}
                {allChecksPass && (
                  <div className="flex">
                    <span
                      className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#15803d] text-white border-2 border-[#15803d]"
                      aria-label="Verification passed"
                    >
                      Pass
                    </span>
                  </div>
                )}
                {/* #846 (CCRS-3940): the fallback path's FAIL counterpart to the
                    Pass indicator above (the OTP-path badges are hidden while
                    `unableTo2fa`). Driven by the SAME live `showFail` as the
                    branch-referral guidance below, so it only shows once every
                    step is complete (#263) and clears if the agent corrects the
                    answer back to Pass (#765). */}
                {showFail && (
                  <div className="flex">
                    <span
                      className="px-6 py-3 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                      aria-label="Verification failed"
                    >
                      Fail
                    </span>
                  </div>
                )}
              </div>
            </InfoBox>
          </>
        )}

        {/* Branch-referral guidance when a check has failed (CCF-106 unhappy
            path). Driven by the LIVE `showFail` (#765, no latch): if the agent
            corrects a mis-clicked answer back to Pass the guidance clears with it,
            matching the reinstated Pass badge + "Continue to dashboard" CTA. A
            terminal 2FA lockout (#639) persists because its own state is sticky.
            #263: only presented once ALL steps (incl. the second factor) are
            complete, not on the first failed answer while a later step is
            outstanding. */}
        {showFail && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4 whitespace-pre-line">
                {eclMediumFailAdvisory}
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 whitespace-pre-line">
                {eclMediumFailRemark}
              </p>
            </div>
          </div>
        )}

        {/* Continue to Dashboard — gated on the LIVE result of THIS ECL run
            (allChecksPass), not the persisted securityLevel, so it doesn't show
            for an already-medium customer or after an answer is changed to Fail. */}
        {allChecksPass && (
          <div className="flex">
            <Button
              type="button"
              onClick={() => void completeAndContinue()}
              aria-label="Continue to dashboard"
              className={primaryBtn}
            >
              Continue to dashboard
            </Button>
          </div>
        )}
      </div>
    </main>
  );
}
